import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { getMetricInfo } from '../../services/calculated-metrics.service';
import api from '../../services/api'; // Import the central api instance
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface MetricInfoModalProps {
  open: boolean;
  onClose: () => void;
  metricName: string | null;
}

export const MetricInfoModal: React.FC<MetricInfoModalProps> = ({ open, onClose, metricName }) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && metricName) {
      const fetchAndLoadContent = async () => {
        setLoading(true);
        setError(null);
        setHtmlContent(null);
        try {
          let content: string;
          // If metricName is a full URL, fetch it directly
          if (metricName.startsWith('http')) {
            const response = await fetch(metricName, {
              headers: { 'Accept': 'text/html' },
            });
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            content = await response.text();
          } else {
            // Otherwise, use the existing logic with getMetricInfo
            const metricDetails = await getMetricInfo(metricName);
            if (metricDetails && metricDetails.path) {
              const response = await api.get(metricDetails.path, {
                headers: { 'Accept': 'text/html' },
              });
              content = response.data;
            } else {
              throw new Error('Path to content not found in metric info.');
            }
          }
          setHtmlContent(content);
        } catch (err) {
          console.error(`Failed to fetch or load content for ${metricName}`, err);
          setError('Не вдалося завантажити інформацію про метрику.');
        } finally {
          setLoading(false);
        }
      };
      fetchAndLoadContent();
    } else if (!open) {
      // Reset state when modal is closed
      setHtmlContent(null);
      setError(null);
    }
  }, [open, metricName]);

  const getMetricTitle = (name: string | null) => {
    switch (name) {
      case 'bmi': return 'Індекс маси тіла (BMI)';
      case 'bmr': return 'Базальний метаболізм (BMR)';
      case 'wdn': return 'Добова норма води (WDN)';
      case 'amr': return 'Активний рівень метаболізму (AMR)';
      case 'whr': return 'Відношення талія/стегна (WHR)';
      case 'bju': return 'Співвідношення БЖУ';
      case 'wdna': return 'Добова норма води для спортсменів (WDNA)';
      default: return 'Інформація про метрику';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-i-doc={ModalIndices.CALCULATED_METRIC_INFO}>
      <DialogTitle>{getMetricTitle(metricName)}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {htmlContent && (
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};
